import {blue, grey} from "@mui/material/colors";
import { createTheme } from '@mui/material/styles';
import { ThemeOptions } from '@mui/material/styles/createTheme';
import "@fontsource/roboto";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

export const defaultTabStyles = { p: 0.5, px: 1.5, minHeight: 42, fontSize: 13, textTransform: 'capitalize', marginBottom: '-2px' }

export const defaultNavigationStyles = {
  padding: 2,
  "&:hover": {
    bgcolor: '#f1f1f1'
  }
}

export const defaultLinkButtonStyles = {
  bgcolor: grey[50]
}

export const defaultBreadcrumbLinkStyles = {
  color: `${grey[700]} ! important`,
  display: 'block'
}

export const defaultBreadcrumbTextStyles = {
  color: `${grey[700]} ! important`,
  cursor: "default"
}

export const DateStyles = {
  width: 30,
  height: 30,
  marginRight: '-1px',
  marginBottom: '-1px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

export const TextFieldStyles = {
  inputProps: { sx: { fontSize: 13, bgcolor: "white", borderRadius: 0 } },
  sx: {
    ".MuiInputBase-root": { borderRadius: 0 },
    ".MuiInputLabel-shrink": { transform: "translate(14px, -9px)" },
  },
};

export const SelectInputProps = {
  sx: {
    fontSize: 12,
    bgcolor: 'white'
  },
}

const themeColors = {
  color: {
    main: '#1652f0',
    bg: grey[900],
    textLight: '#ffffff',
  },
} as const;

const themeOptions: ThemeOptions = {
  ...themeColors,
  palette: {
    primary: {
      main: themeColors.color.bg,
    },
    blue: {
      main: blue[800],
      light: blue[700],
      dark: blue[900],
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {},
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: '0 1px 1px 0 #001c244d, 1px 1px 1px 0 #001c2426, -1px 1px 1px 0 #001c2426',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: 'none !important',
          '.MuiDateRangeCalendar-monthContainer': {
            backgroundColor: 'white',
            zIndex: 999999
          },
          '.MuiFormControl-root': {
            '.MuiInputBase-root': {
              fontSize: 13
            },
            '.MuiNativeSelect-select option': {
              fontSize: 13
            }
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          '.MuiListItemText-root span': {
            fontSize: 13
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          "& .MuiMenuItem-root": {
            fontSize: 13
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: 13
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          '&.MuiTooltip-popperArrow[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
            marginTop: "-1.25em",
          },
        },
        tooltip: {
          borderRadius: 0,
          padding: "8px",
          color: grey[900],
          backgroundColor: "#ffffff",
          border: `1px solid ${grey[500]}`,
          fontWeight: "normal",
        },
        arrow: {
          width: "1.75em",
          height: "1.25em",
          color: "#ffffff",
          "&:before": {
            border: `1px solid ${grey[500]}`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderColor: blue[900],
            },
          },
        }
      }
    },
    MuiButtonGroup: {
      styleOverrides: {
        firstButton: {
          "&:hover": {
            
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: grey[600],
          '&.Mui-checked': {
            color: blue[900],
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#1565c0',
          },
        },
        track: {
          backgroundColor: grey[500],
          '.Mui-checked.Mui-checked + &': {
            backgroundColor: '#0d47a1',
          },
        },
      },
    }
  },
};

type CustomTheme = {
  [Key in keyof typeof themeColors]: typeof themeColors[Key];
};

declare module '@mui/material/styles' {
  interface Palette {
    blue: Palette['primary'];
  }

  interface PaletteOptions {
    blue?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    blue: true;
  }
}

declare module '@mui/material/styles/createTheme' {
  interface Theme extends CustomTheme { }
  interface ThemeOptions extends CustomTheme { }
  type ButtonPropsColorOverrides = "custom"
}

export const theme = createTheme({ ...themeColors, ...themeOptions });